'use client';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

/* -------------------------------------------------------------------------- */
/*  Data — every telephony / voice provider with a logo                       */
/* -------------------------------------------------------------------------- */

interface Tile {
  name: string;
  src: string;
  /** Slug for /telephony/[slug] — must match a provider in telephony-data.ts */
  slug: string;
}

/**
 * Local SVGs first, then Brandfetch CDN (https://cdn.brandfetch.io/{domain})
 * for everything else. Brandfetch publicly serves brand marks by domain.
 */
const TELEPHONY: Tile[] = [
  // Local SVGs (handcrafted, monochrome-friendly)
  { name: 'Twilio', src: '/telephony/twilio.svg', slug: 'twilio' },
  {
    name: 'RingCentral',
    src: '/telephony/ringcentral.svg',
    slug: 'ringcentral',
  },
  { name: 'Zoom Phone', src: '/telephony/zoom.svg', slug: 'zoom-phone' },
  { name: 'Vonage', src: '/telephony/vonage.svg', slug: 'vonage' },
  { name: 'Aircall', src: '/telephony/aircall.svg', slug: 'aircall' },
  { name: 'Dialpad', src: '/telephony/dialpad.svg', slug: 'dialpad' },
  { name: 'Five9', src: '/telephony/five9.svg', slug: 'five9' },
  { name: 'Genesys', src: '/telephony/genesys.svg', slug: 'genesys' },

  // Brandfetch CDN
  { name: 'Cisco Webex', src: bf('webex.com'), slug: 'cisco-webex' },
  {
    name: 'Microsoft Teams',
    src: bf('microsoft.com'),
    slug: 'microsoft-teams',
  },
  { name: 'Amazon Connect', src: bf('aws.amazon.com'), slug: 'amazon-connect' },
  { name: 'Google Voice', src: bf('google.com'), slug: 'google-voice' },
  { name: 'NICE CXone', src: bf('nice.com'), slug: 'nice-cxone' },
  { name: 'Talkdesk', src: bf('talkdesk.com'), slug: 'talkdesk' },
  { name: 'Avaya', src: bf('avaya.com'), slug: 'avaya' },
  { name: 'Mitel', src: bf('mitel.com'), slug: 'mitel' },
  { name: '8x8', src: bf('8x8.com'), slug: '8x8' },
  { name: 'Nextiva', src: bf('nextiva.com'), slug: 'nextiva' },
  { name: 'GoTo Connect', src: bf('goto.com'), slug: 'goto-connect' },
  { name: 'Freshcaller', src: bf('freshworks.com'), slug: 'freshcaller' },
  { name: 'JustCall', src: bf('justcall.io'), slug: 'justcall' },
  { name: 'CloudTalk', src: bf('cloudtalk.io'), slug: 'cloudtalk' },
  { name: 'Ooma', src: bf('ooma.com'), slug: 'ooma' },
  { name: 'Grasshopper', src: bf('grasshopper.com'), slug: 'grasshopper' },
  { name: 'Bandwidth', src: bf('bandwidth.com'), slug: 'bandwidth' },
  { name: 'Plivo', src: bf('plivo.com'), slug: 'plivo' },
  { name: 'Sinch', src: bf('sinch.com'), slug: 'sinch' },
  { name: 'Telnyx', src: bf('telnyx.com'), slug: 'telnyx' },
  { name: 'SignalWire', src: bf('signalwire.com'), slug: 'signalwire' },
  { name: 'Skype', src: bf('skype.com'), slug: 'skype' },
  { name: '3CX', src: bf('3cx.com'), slug: '3cx' },
  { name: 'Asterisk', src: bf('asterisk.org'), slug: 'asterisk' },
  { name: 'FreePBX', src: bf('freepbx.org'), slug: 'freepbx' },
  { name: 'BT Business', src: bf('bt.com'), slug: 'bt-business' },
  {
    name: 'Verizon Business',
    src: bf('verizon.com'),
    slug: 'verizon-business',
  },
  { name: 'AT&T Business', src: bf('att.com'), slug: 'att-business' },
  { name: 'Orange Business', src: bf('orange.com'), slug: 'orange-business' },
];

function bf(domain: string) {
  return `https://cdn.brandfetch.io/${domain}/w/200/h/200`;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export const TelephonyWall = () => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <section className="bg-[#fafaf9] py-20 md:py-28">
      <div className="container">
        {/* Header */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <h2 className="font-serif text-balance text-neutral-900 text-2xl md:text-4xl">
            {isAr
              ? 'اربطه بأي نظام هاتف في العالم.'
              : 'Plug into any phone system on Earth.'}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-neutral-500 md:text-base">
            {isAr
              ? 'Cloud PBX، حزمة مركز اتصال، SIP trunk، PSTN قديم — إذا كان يُجري مكالمة، Open يرد عليها.'
              : 'Cloud PBX, contact-center suite, SIP trunk, legacy PSTN — if it makes a phone call, Open answers on it.'}
          </p>
          <p className="mx-auto mt-3 max-w-xl text-xs text-neutral-400 md:text-sm">
            {isAr
              ? 'اضغط على أي مزوّد لعرض دليل الربط.'
              : 'Click any provider for an integration guide.'}
          </p>
        </div>

        {/* Logo wall */}
        <TooltipProvider delayDuration={120} skipDelayDuration={150}>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-6">
            {TELEPHONY.map((tile) => (
              <Cell key={tile.name} tile={tile} isAr={isAr} />
            ))}
          </div>
        </TooltipProvider>
      </div>
    </section>
  );
};

const Cell = ({ tile, isAr }: { tile: Tile; isAr: boolean }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Link
        href={`/telephony/${tile.slug}`}
        prefetch={false}
        aria-label={
          isAr ? `دليل ربط ${tile.name}` : `${tile.name} integration guide`
        }
        className="group relative flex aspect-[3/2] flex-col items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-4 transition-colors duration-200 hover:border-neutral-300"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={tile.src}
          alt={tile.name}
          loading="lazy"
          className="max-h-8 w-auto max-w-[75%] object-contain opacity-75 transition-opacity duration-200 group-hover:opacity-100"
        />
        <span className="text-[10px] font-medium text-neutral-500 transition-colors duration-200 group-hover:text-neutral-900 sm:text-[11px]">
          {tile.name}
        </span>
      </Link>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      sideOffset={6}
      className="border-neutral-800 bg-neutral-900 text-xs font-medium text-white"
    >
      {isAr
        ? `دليل ربط Open مع ${tile.name} ←`
        : `Open ${tile.name} integration guide →`}
    </TooltipContent>
  </Tooltip>
);

export default TelephonyWall;
